import React, {
  useState, useCallback, useEffect, useRef,
} from 'react';
import styled from 'styled-components';
import { useHistory } from 'react-router-dom';
import { useDropzone } from 'react-dropzone';
import Select from 'react-select';
import { useQuery, useMutation } from '@apollo/react-hooks';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import Button from '../Button';
import Error from '../Error';
import { getErrorMessage } from '../../util/ErrorUtil';
import { UPLOAD_FILES, GET_TIMEZONES } from '../../graphql/queries';
import { IconEnum as Icons } from '../Icons';
import NewAccountScreenLayout from './NewAccountScreenLayout';
import colors from '../../styles/colors';

import step3Image from '../../assets/images/new-account/03.jpg';

const Form = styled.form`
  display: flex;
  flex-direction: column;
  width: 100%;
  font-size: 1.2rem;
`;

const DropzoneText = styled.span`
  font-weight: 500;
  font-size: 1.2rem;
  color: ${colors.seaGreen};
  text-decoration: underline;
  cursor: pointer;
`;

/** Circular container for logo - matches design (03.png) */
const LogoContainer = styled.div`
  width: 100px;
  height: 100px;
  border-radius: 50%;
  background: ${colors.white};
  border: 1px solid ${colors.grey5};
  box-shadow: 0px 4px 16px rgba(0, 0, 0, 0.05);
  overflow: hidden;
  display: flex;
  align-items: center;
  justify-content: center;
  cursor: pointer;
  flex-shrink: 0;
`;

/** Rectangular container for poster - matches design (03.png) */
const PosterContainer = styled.div`
  width: 160px;
  height: 100px;
  border-radius: 5px;
  background: ${colors.white};
  border: 1px solid ${colors.grey5};
  box-shadow: 0px 4px 16px rgba(0, 0, 0, 0.05);
  overflow: hidden;
  display: flex;
  align-items: center;
  justify-content: center;
  cursor: pointer;
  flex-shrink: 0;
`;

const PreviewImg = styled.img`
  width: 100%;
  height: 100%;
  object-fit: cover;
`;

const UploadBlock = styled.div`
  margin: 16px 0;
`;

const UploadRow = styled.div`
  display: flex;
  align-items: flex-start;
  gap: 16px;
  flex-wrap: wrap;
`;

const HelpText = styled.p`
  margin: 0;
  font-size: 1.2rem;
  color: ${colors.grey3};
  line-height: 1.5;
  flex: 1;
  min-width: 200px;
`;

const HelpTextBold = styled.span`
  font-weight: 600;
  color: ${colors.grey2};
`;

/** Match design: uppercase labels, 1.2rem, grey3, 30px 0 10px 0 */
const FieldLabel = styled.div`
  font-size: 1.2rem;
  color: ${colors.grey3};
  margin: 30px 0px 10px 0px;
  text-transform: uppercase;
`;

const FieldLabelFirst = styled(FieldLabel)`
  margin-top: 0;
`;

/** Time zone dropdown: not full-width per design (03.png) */
const TimeZoneWrapper = styled.div`
  max-width: 400px;
  width: 100%;
`;

const colourStyles = {
  control: (base) => ({
    ...base,
    minHeight: '40px',
    border: `1px solid ${colors.grey5}`,
    borderRadius: '5px',
    backgroundColor: colors.white,
    width: '100%',
    '&:hover': { borderColor: colors.grey5 },
  }),
  menu: (base) => ({ ...base, zIndex: 9999 }),
  menuPortal: (base) => ({ ...base, zIndex: 9999 }),
};

const STORAGE_KEY_BUSINESS = 'newAccount_businessOwner';
const STORAGE_KEY_STEP3 = 'newAccount_step3';

function getBusinessOwnerFromStorage() {
  try {
    const raw = sessionStorage.getItem(STORAGE_KEY_BUSINESS);
    return raw ? JSON.parse(raw) : null;
  } catch {
    return null;
  }
}

function getStep3FromStorage() {
  try {
    const raw = sessionStorage.getItem(STORAGE_KEY_STEP3);
    return raw ? JSON.parse(raw) : null;
  } catch {
    return null;
  }
}

function saveStep3ToStorage(data) {
  try {
    sessionStorage.setItem(STORAGE_KEY_STEP3, JSON.stringify(data));
  } catch (err) {
    // ignore
  }
}

/** Default image URLs from public folder - no upload, use URL directly for createOrganization */
function getDefaultLogoUrl() {
  if (typeof window === 'undefined') return '';
  const base = (process.env.PUBLIC_URL || '').replace(/\/$/, '');
  return `${window.location.origin}${base}/new-account/logo.png`;
}

function getDefaultPosterUrl() {
  if (typeof window === 'undefined') return '';
  const base = (process.env.PUBLIC_URL || '').replace(/\/$/, '');
  return `${window.location.origin}${base}/new-account/poster.png`;
}

export default function OrganizationImages({
  businessOwnerValues: businessOwnerValuesProp,
  onSuccess,
}) {
  const history = useHistory();
  const businessOwnerValues = businessOwnerValuesProp || getBusinessOwnerFromStorage();
  const uploadTargetRef = useRef(null);

  React.useEffect(() => {
    if (!businessOwnerValues?.name) {
      history.replace('/new-account/step/2');
    }
  }, [businessOwnerValues, history]);

  const { data: timezonesData } = useQuery(GET_TIMEZONES);
  const storedStep3 = getStep3FromStorage();
  const [timeZoneObj, setTimeZoneObj] = useState(null);
  const [logoUrl, setLogoUrl] = useState(storedStep3?.logoUrl || '');
  const [posterImageUrl, setPosterImageUrl] = useState(storedStep3?.posterImageUrl || '');
  const [errorMsg, setErrorMsg] = useState('');

  useEffect(() => {
    if (timezonesData?.timezones?.defaultTimezone) {
      const defaultTz = timezonesData.timezones.defaultTimezone;
      if (storedStep3?.timezoneId && timezonesData.timezones.timezonesArray) {
        const found = timezonesData.timezones.timezonesArray.find(
          (t) => t.value === storedStep3.timezoneId,
        );
        setTimeZoneObj(found || defaultTz);
      } else {
        setTimeZoneObj(defaultTz);
      }
    }
  }, [timezonesData, storedStep3?.timezoneId]);

  const [uploadFiles] = useMutation(UPLOAD_FILES, {
    onError(err) {
      setErrorMsg(getErrorMessage(err));
    },
    onCompleted(data) {
      if (data?.uploadFiles?.[0]?.url) {
        const target = uploadTargetRef.current;
        if (target === 'logo') {
          setLogoUrl(data.uploadFiles[0].url);
        } else if (target === 'poster') {
          setPosterImageUrl(data.uploadFiles[0].url);
        }
        uploadTargetRef.current = null;
      }
    },
  });

  const onDropLogo = useCallback(
    (acceptedFiles) => {
      if (acceptedFiles.length) {
        uploadTargetRef.current = 'logo';
        uploadFiles({ variables: { files: acceptedFiles } });
      }
    },
    [uploadFiles],
  );

  const onDropPoster = useCallback(
    (acceptedFiles) => {
      if (acceptedFiles.length) {
        uploadTargetRef.current = 'poster';
        uploadFiles({ variables: { files: acceptedFiles } });
      }
    },
    [uploadFiles],
  );

  const logoDropzone = useDropzone({
    onDrop: onDropLogo,
    accept: 'image/jpeg, image/png',
    multiple: false,
    noClick: false,
    noKeyboard: false,
  });

  const posterDropzone = useDropzone({
    onDrop: onDropPoster,
    accept: 'image/jpeg, image/png',
    multiple: false,
    noClick: false,
    noKeyboard: false,
  });

  const handleUseDefaultLogo = useCallback(() => {
    setErrorMsg('');
    setLogoUrl(getDefaultLogoUrl());
  }, []);

  const handleUseDefaultPoster = useCallback(() => {
    setErrorMsg('');
    setPosterImageUrl(getDefaultPosterUrl());
  }, []);

  const handleSubmit = (e) => {
    e.preventDefault();
    setErrorMsg('');
    const tzId = timeZoneObj?.value || timezonesData?.timezones?.defaultTimezone?.value;
    if (!tzId) {
      setErrorMsg('Please select a time zone');
      return;
    }
    if (!logoUrl) {
      setErrorMsg('Please upload a logo or use the default logo');
      return;
    }
    if (!posterImageUrl) {
      setErrorMsg('Please upload a poster image or use the default poster');
      return;
    }
    saveStep3ToStorage({ timezoneId: tzId, logoUrl, posterImageUrl });
    if (onSuccess) onSuccess();
    else history.push('/new-account/step/4');
  };

  const handleBack = () => {
    history.push('/new-account/step/2');
  };

  const timezoneOptions = timezonesData?.timezones?.timezonesArray || [];
  const defaultTz = timezonesData?.timezones?.defaultTimezone;

  const footer = (
    <>
      <Button type="button" text="Back" onClick={handleBack} width="auto" />
      <Button
        margin="0"
        text="Next"
        type="button"
        onClick={() => document.getElementById('new-account-step3-form')?.requestSubmit()}
        width="auto"
      />
    </>
  );

  return (
    <NewAccountScreenLayout
      stepNumber={3}
      screenImageSrc={step3Image}
      title="Personalize Your Account"
      showSignInLink={false}
      footer={footer}
    >
      <Form id="new-account-step3-form" onSubmit={handleSubmit}>
        <FieldLabelFirst>Time Zone</FieldLabelFirst>
        <TimeZoneWrapper>
          <Select
            value={timeZoneObj || defaultTz}
            onChange={(val) => setTimeZoneObj(val)}
            options={timezoneOptions}
            styles={colourStyles}
            menuPortalTarget={typeof document !== 'undefined' ? document.body : null}
          />
        </TimeZoneWrapper>

        <UploadBlock>
          <FieldLabel>Upload Logo</FieldLabel>
          <UploadRow>
            <LogoContainer {...logoDropzone.getRootProps()}>
              <input {...logoDropzone.getInputProps()} />
              {logoUrl ? (
                <PreviewImg src={logoUrl} alt="Logo" />
              ) : (
                <FontAwesomeIcon
                  icon={Icons.UploadRegular}
                  size="2x"
                  color={colors.grey4}
                  style={{ opacity: 0.8 }}
                />
              )}
            </LogoContainer>
            <HelpText>
              <HelpTextBold>Recommendation:</HelpTextBold>
              <p>Upload a Logo with 200x200px dimensions.</p>
            </HelpText>
          </UploadRow>
          <div style={{ marginTop: 8 }}>
            <DropzoneText
              onClick={(e) => {
                e.preventDefault();
                handleUseDefaultLogo();
              }}
            >
              Use default logo
            </DropzoneText>
          </div>
        </UploadBlock>

        <UploadBlock>
          <FieldLabel>Upload Poster Image</FieldLabel>
          <UploadRow>
            <PosterContainer {...posterDropzone.getRootProps()}>
              <input {...posterDropzone.getInputProps()} />
              {posterImageUrl ? (
                <PreviewImg src={posterImageUrl} alt="Poster" />
              ) : (
                <FontAwesomeIcon
                  icon={Icons.UploadRegular}
                  size="2x"
                  color={colors.grey4}
                  style={{ opacity: 0.8 }}
                />
              )}
            </PosterContainer>
            <HelpText>
              <HelpTextBold>Recommendation:</HelpTextBold>
              <p>Upload a cover image within 1080px wide & less than 3MB in size.</p>
            </HelpText>
          </UploadRow>
          <div style={{ marginTop: 8 }}>
            <DropzoneText
              onClick={(e) => {
                e.preventDefault();
                handleUseDefaultPoster();
              }}
            >
              Use default poster
            </DropzoneText>
          </div>
        </UploadBlock>

        {errorMsg && <Error error={errorMsg} margin="16px 0 0 0" />}
      </Form>
    </NewAccountScreenLayout>
  );
}
